
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Poll } from "@/types/poll";
import { Button } from "@/components/ui/button";
import { MessageSquare } from "lucide-react";
import { SocialMetrics } from "@/types/social";
import { useCommentCount } from "@/contexts/comments/CommentCountContext";
import LikeButton from "@/components/social/LikeButton";
import ShareButton from "@/components/social/ShareButton";
import UniversalPollDisplay from "@/components/polls/shared/UniversalPollDisplay";
import TrendingBadge from "@/components/social/TrendingBadge";
import { usePollSocialMetrics } from "@/hooks/usePollSocialMetrics";

interface PollVotingCardProps {
  polls: Poll[];
  setPolls: React.Dispatch<React.SetStateAction<Poll[]>>;
  userVotes: Record<string, number>;
  setUserVotes: React.Dispatch<React.SetStateAction<Record<string, number>>>;
  currentPoll: Poll;
  hasVoted: boolean;
  canVote: boolean;
  socialMetrics?: SocialMetrics;
  onCommentClick: () => void;
  markRecentVote?: (pollId: string) => void;
  onRefresh?: () => void;
}

const PollVotingCard = ({
  polls,
  setPolls,
  userVotes,
  setUserVotes,
  currentPoll,
  hasVoted,
  canVote,
  socialMetrics,
  onCommentClick,
  markRecentVote,
  onRefresh
}: PollVotingCardProps) => {
  // Get poll social metrics for trending badge
  const { metrics } = usePollSocialMetrics(currentPoll?.id);
  
  // Get comment count from context when available
  let commentsCount = socialMetrics?.commentsCount || 0;
  
  try {
    const { commentCount } = useCommentCount();
    commentsCount = commentCount;
  } catch (error) {
    // Context not available, use props
    console.log("Comment count context not available in PollVotingCard");
  }
  
  return (
    <Card className="w-full overflow-hidden">
      <CardHeader className="pb-4">
        <div className="flex justify-between items-start">
          <CardTitle className="text-lg sm:text-xl flex-1">
            {hasVoted ? "Your Response" : "Cast Your Vote"}
          </CardTitle>
          <TrendingBadge 
            score={metrics?.heatScore || 0}
            size="sm"
          />
        </div>
      </CardHeader>
      <CardContent className="space-y-6 overflow-hidden">
        <div className="w-full overflow-hidden">
          <UniversalPollDisplay
            polls={polls}
            setPolls={setPolls}
            userVotes={userVotes}
            setUserVotes={setUserVotes}
            currentPoll={currentPoll}
            markRecentVote={markRecentVote}
            onRefresh={onRefresh}
            variant="default"
            showShareButtons={false}
            showTrendingBadge={false} // Hide in UniversalPollDisplay since we show it in header
          />
        </div>
        
        {/* Social interaction bar */}
        <div className="pt-4 border-t border-gray-100 dark:border-gray-800 w-full overflow-hidden">
          <div className="flex flex-wrap items-center justify-start gap-2 w-full overflow-x-auto">
            <div className="flex-shrink-0">
              <LikeButton 
                cardId={currentPoll.id}
                initialLikes={socialMetrics?.likesCount || 0}
                showCount={true}
                size="sm"
                contentType="poll"
              />
            </div>
            
            <div className="flex-shrink-0">
              <ShareButton
                contentId={currentPoll.id}
                contentType="poll"
                initialShares={socialMetrics?.sharesCount || 0}
                showCount={true}
                size="sm"
              />
            </div>
            
            <Button 
              variant="outline" 
              size="sm" 
              onClick={onCommentClick}
              className="flex-shrink-0 whitespace-nowrap"
            >
              <MessageSquare className="h-4 w-4 mr-1 flex-shrink-0" />
              <span className="truncate">All Comments ({commentsCount})</span>
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PollVotingCard;
